import React, { useState } from 'react'
import {
  Industries,
  Departments,
  Scales,
  DocumentTypes
} from '@/types/newt/voice'

interface FilterSectionProps {
  industries: Industries[]
  departments: Departments[]
  scales: Scales[]
  documentTypes: DocumentTypes[]
  selectedIndustries: string[]
  setSelectedIndustries: React.Dispatch<React.SetStateAction<string[]>>
  selectedScales: string[]
  setSelectedScales: React.Dispatch<React.SetStateAction<string[]>>
  selectedDepartments: string[]
  setSelectedDepartments: React.Dispatch<React.SetStateAction<string[]>>
  selectedDocumentTypes: string[]
  setSelectedDocumentTypes: React.Dispatch<React.SetStateAction<string[]>>
  handleFilter: () => void
  filteredVoiceCount: number
}

const SelectableButton: React.FC<{
  isSelected: boolean
  onClick: () => void
  children: React.ReactNode
}> = ({ isSelected, onClick, children }) => {
  return (
    <button
      className={`
        relative text-sm lg:text-base leading-[1.7] lg:leading-loose px-1 rounded-[4px]
        ${
          isSelected
            ? 'border border-[#7FB2F0] bg-[#F2F8FE]'
            : 'border border-white'
        }
      `}
      onClick={onClick}
    >
      {children}
      {isSelected && (
        <span className="absolute flex justify-center items-center w-[16px] h-[16px] top-[-8px] right-[-8px] rounded-full bg-[#ededed]">
          <svg
            width="6"
            height="6"
            viewBox="0 0 6 6"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M4.76259e-06 0.5L0.500005 5.47909e-07L6 5.5L5.5 6L4.76259e-06 0.5Z"
              fill="#424242"
            ></path>
            <path d="M5.5 0L6 0.5L0.5 6L0 5.5L5.5 0Z" fill="#424242"></path>
          </svg>
        </span>
      )}
    </button>
  )
}

const CategorySection: React.FC<{
  title: string
  items: { slug: string; name: string }[]
  selectedItems: string[]
  setSelectedItems: React.Dispatch<React.SetStateAction<string[]>>
  gridClass: string
  setGridClass: React.Dispatch<React.SetStateAction<string>>
  ariaLabel: string
  setAriaLabel: React.Dispatch<React.SetStateAction<string>>
}> = ({
  title,
  items,
  selectedItems,
  setSelectedItems,
  gridClass,
  setGridClass,
  ariaLabel,
  setAriaLabel
}) => {
  const toggleSelection = (item: { slug: string; name: string }) => {
    setSelectedItems((prev) =>
      prev.includes(item.slug)
        ? prev.filter((slug) => slug !== item.slug)
        : [...prev, item.slug]
    )
  }

  return (
    <div className="border-t border-gray">
      <div className="lg:flex lg:items-center">
        <div className="relative">
          <h3 className="lg:w-[147px] text-base font-bold py-2 px-4 lg:pl-10">
            {title}
          </h3>
          <button
            className="absolute w-full h-full top-0 left-0 lg:hidden"
            aria-label={ariaLabel}
            onClick={() => {
              if (gridClass === 'grid-rows-[0fr]') {
                setGridClass('grid-rows-[1fr]')
                setAriaLabel('閉じる')
              } else {
                setGridClass('grid-rows-[0fr]')
                setAriaLabel('開く')
              }
            }}
          >
            <span className="absolute w-[24px] lg:w-[36px] h-[24px] lg:h-[36px] right-4 lg:right-[2.5rem] top-1/2 -mt-3 lg:mt-[-18px] bg-primary rounded-full">
              <span className="w-[1px] h-[10px] lg:h-4 bg-white block absolute top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2 transition-transform duration-300"></span>
              <span className="w-[10px] lg:w-[16px] h-[1px] bg-white block absolute top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2"></span>
            </span>
          </button>
        </div>
        <div
          className={`grid lg:block lg:flex-1 transition-[grid-template-rows] duration-300 ${gridClass}`}
        >
          <div className="overflow-hidden">
            <ul className="flex flex-wrap gap-x-[10px] gap-y-[1px] px-3 py-2 border-t border-gray lg:border-t-0">
              {items.map((item) => (
                <li key={item.slug}>
                  <SelectableButton
                    isSelected={selectedItems.includes(item.slug)}
                    onClick={() => toggleSelection(item)}
                  >
                    {item.name}
                  </SelectableButton>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  )
}

export function FilterSection({
  industries,
  departments,
  scales,
  documentTypes,
  selectedIndustries,
  setSelectedIndustries,
  selectedScales,
  setSelectedScales,
  selectedDepartments,
  setSelectedDepartments,
  selectedDocumentTypes,
  setSelectedDocumentTypes,
  handleFilter,
  filteredVoiceCount
}: FilterSectionProps) {
  const [gridClass, setGridClass] = useState('lg:grid-rows-[0fr]')
  const [gridClass2, setGridClass2] = useState('grid-rows-[0fr]')
  const [gridClass3, setGridClass3] = useState('grid-rows-[0fr]')
  const [gridClass4, setGridClass4] = useState('grid-rows-[0fr]')
  const [gridClass5, setGridClass5] = useState('grid-rows-[0fr]')
  const [ariaLabel, setAriaLabel] = useState('開く')
  const [ariaLabel2, setAriaLabel2] = useState('開く')
  const [ariaLabel3, setAriaLabel3] = useState('開く')
  const [ariaLabel4, setAriaLabel4] = useState('開く')
  const [ariaLabel5, setAriaLabel5] = useState('開く')

  return (
    <div className="bg-white rounded-lg border border-gray shadow-[0_4px_24px_rgba(199,189,168,.08)]">
      <div className="relative">
        <h2 className="font-bold text-lg lg:text-2xl leading-normal pt-4 pb-2 px-4 lg:px-10 lg:py-6">
          条件で絞り込む
        </h2>
        <button
          className="absolute w-full h-full top-0 left-0 hidden lg:block"
          aria-label={ariaLabel}
          onClick={() => {
            if (gridClass === 'lg:grid-rows-[0fr]') {
              setGridClass('lg:grid-rows-[1fr]')
              setAriaLabel('閉じる')
            } else {
              setGridClass('lg:grid-rows-[0fr]')
              setAriaLabel('開く')
            }
          }}
        >
          <span className="absolute w-[24px] lg:w-[36px] h-[24px] lg:h-[36px] right-4 lg:right-[2.5rem] top-1/2 -mt-3 lg:mt-[-18px] bg-primary rounded-full">
            <span className="w-[1px] h-[10px] lg:h-[16px] bg-white block absolute top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2 transition-transform duration-300"></span>
            <span className="w-[10px] lg:w-[16px] h-[1px] bg-white block absolute top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2"></span>
          </span>
        </button>
      </div>
      <div
        className={`sm:grid lg:transition-[grid-template-rows] lg:duration-300 ${gridClass}`}
      >
        <div className="overflow-hidden">
          <CategorySection
            title="業種"
            items={industries}
            selectedItems={selectedIndustries}
            setSelectedItems={setSelectedIndustries}
            gridClass={gridClass2}
            setGridClass={setGridClass2}
            ariaLabel={ariaLabel2}
            setAriaLabel={setAriaLabel2}
          />
          <CategorySection
            title="導入部署"
            items={departments}
            selectedItems={selectedDepartments}
            setSelectedItems={setSelectedDepartments}
            gridClass={gridClass3}
            setGridClass={setGridClass3}
            ariaLabel={ariaLabel3}
            setAriaLabel={setAriaLabel3}
          />
          <CategorySection
            title="企業規模"
            items={scales}
            selectedItems={selectedScales}
            setSelectedItems={setSelectedScales}
            gridClass={gridClass4}
            setGridClass={setGridClass4}
            ariaLabel={ariaLabel4}
            setAriaLabel={setAriaLabel4}
          />
          <CategorySection
            title="文書類型"
            items={documentTypes}
            selectedItems={selectedDocumentTypes}
            setSelectedItems={setSelectedDocumentTypes}
            gridClass={gridClass5}
            setGridClass={setGridClass5}
            ariaLabel={ariaLabel5}
            setAriaLabel={setAriaLabel5}
          />
          <div className="lg:pb-5">
            <div className="relative text-center mt-[7px] lg:mt-0 border-t border-gray lg:border-t-0 py-4 lg:py-5">
              <button
                className="relative z-10 text-sm text-primary leading-normal rounded-full border border-primary border-solid text-center inline-block font-bold transition hover:text-secondary hover:border-secondary px-6 h-[36px]"
                onClick={handleFilter}
              >
                選択した条件で絞り込む
              </button>
              <p className="lg:absolute lg:left-[40px] lg:top-0 lg:w-full lg:h-full lg:flex lg:items-center text-sm lg:text-base leading-[1.7] lg:leading-[2] mt-4 lg:mt-0">
                検索結果：{filteredVoiceCount} 件
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
